import { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

/** Assistant IA — rédige la justification professionnelle et factuelle + recommandations (toujours relu et modifiable par le coach). */
export function AssessmentAiAssist({ dossierId, form, onApply }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const filled = Object.values(form.criteria || {}).filter(Boolean).length;
  const run = async () => {
    if (filled < 3 && !form.observed_strengths.trim() && !notes.trim()) { toast.error("Coche au moins 3 critères ou note une observation avant de demander l'aide IA."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/trial-integration/dossiers/${dossierId}/assessment/ai-draft`, {
        criteria: form.criteria, observed_strengths: form.observed_strengths, support_needed: form.support_needed, goal_program_fit: form.goal_program_fit,
        recommended_group: form.recommended_group, integration_decision: form.integration_decision, plan_start_details: form.plan_start_details, decision_summary: form.decision_summary, coach_notes: notes,
      });
      setRes(r.data);
      onApply({ decision_summary: r.data.decision_summary });
      toast.success("Brouillon rédigé — relis, ajuste, puis enregistre.");
    } catch (e) { toast.error(e.response?.data?.detail || "IA indisponible"); }
    setBusy(false);
  };
  return (
    <div className="rounded-lg border border-violet-500/30 bg-violet-500/[0.05] p-3 space-y-2" data-testid="ti-ai-assist">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-[10px] tracking-[0.25em] text-violet-300 font-semibold">✨ AIDE À LA RÉDACTION (IA)</div>
        <span className="text-[11px] text-zinc-500">Transforme tes observations en justification professionnelle, factuelle et propre — sans diagnostic ni jugement.</span>
      </div>
      <div className="flex gap-2">
        <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes libres du coach (facultatif) : ex. « a demandé 3 rappels pour attendre le signal, très motivé en fin de cours »"
          className="flex-1 bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-xs text-zinc-100" data-testid="ti-ai-notes" />
        <button type="button" onClick={run} disabled={busy} className="px-3 py-2 rounded-md bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-xs font-semibold whitespace-nowrap" data-testid="ti-ai-run">
          {busy ? "Rédaction…" : res ? "↻ Réécrire" : "Rédiger avec l'IA"}
        </button>
      </div>
      {res && (
        <div className="space-y-2" data-testid="ti-ai-result">
          {res.caution && <div className="text-[11px] text-amber-300 border border-amber-500/40 rounded-md px-2 py-1.5" data-testid="ti-ai-caution">⚠️ {res.caution}</div>}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Ce qui pourrait être recommandé</div>
            <ul className="space-y-1">
              {res.recommendations.map((r, i) => <li key={i} className="text-xs text-zinc-200 flex gap-2" data-testid={`ti-ai-reco-${i}`}><span className="text-violet-300">•</span><span>{r}</span></li>)}
            </ul>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {res.next_action && <button type="button" onClick={() => { onApply({ next_action: res.next_action }); toast.success("Prochaine action appliquée"); }} className="text-[11px] px-2 py-1 rounded border border-zinc-700 text-zinc-300 hover:border-violet-400" data-testid="ti-ai-apply-next">➜ Prochaine action : « {res.next_action} »</button>}
            {res.recommended_group && <button type="button" onClick={() => { onApply({ recommended_group: res.recommended_group }); toast.success("Groupe appliqué"); }} className="text-[11px] px-2 py-1 rounded border border-zinc-700 text-zinc-300 hover:border-violet-400" data-testid="ti-ai-apply-group">👥 Groupe : {res.recommended_group}</button>}
            {res.plan_start_details && <button type="button" onClick={() => { onApply({ plan_start_details: res.plan_start_details }); toast.success("Plan de départ appliqué"); }} className="text-[11px] px-2 py-1 rounded border border-zinc-700 text-zinc-300 hover:border-violet-400" data-testid="ti-ai-apply-plan">📋 Appliquer le plan de départ</button>}
          </div>
          <div className="text-[10px] text-zinc-500">Le coach reste responsable du contenu : relis et ajuste avant d'enregistrer.</div>
        </div>
      )}
    </div>
  );
}
